import { useState, useEffect, useCallback } from "react";
import { WashingMachine, Wifi, AirVent, Coffee, Microwave, Heater, Wind, Layers, CookingPot, Droplet, X, ChevronLeft, ChevronRight } from "lucide-react";

const features = [
  {
    tag: "01 / SPAZIO",
    title: "60 mq Luminosi",
    text: "Ambienti ariosi nel borgo antico con soffitti d'epoca, cucina attrezzata e soggiorno accogliente.",
  },
  {
    tag: "02 / OSPITALITÀ",
    title: "Fino a 4 Ospiti",
    text: "1 camera matrimoniale padronale e 1 stanza singola con terzo letto estraibile per famiglie o coppie.",
  },
  {
    tag: "03 / ATMOSFERA",
    title: "Vino e Tradizione",
    text: "Nel cuore della sagra del vino, tra osterie tipiche e vicoli suggestivi in pietra vulcanica.",
  },
];

const photos = [
  {
    src: "/photos/camera-matrimoniale.jpg",
    alt: "Camera matrimoniale di Borgo DiVino con letto matrimoniale e finestre luminose",
    caption: "Camera matrimoniale principale",
    testid: "photo-bedroom",
    position: "object-[center_72%]",
  },
  {
    src: "/photos/camera-singola.jpg",
    alt: "Stanza singola di Borgo DiVino con letto singolo e terzo letto estraibile",
    caption: "Stanza singola con terzo letto estraibile",
    testid: "photo-single",
  },
];

const extraPhotos = [
  {
    src: "/photos/soggiorno.jpg",
    alt: "Soggiorno luminoso di Borgo DiVino con divano e zona cucina",
    caption: "Soggiorno e zona giorno luminosa",
    testid: "photo-living",
  },
  {
    src: "/photos/bagno.jpg",
    alt: "Bagno di Borgo DiVino con doccia in vetro e lavatrice",
    caption: "Bagno con doccia e lavatrice",
    testid: "photo-bathroom",
    position: "object-[center_40%]",
  },
];

const gallery = [...photos, ...extraPhotos];

const amenities = [
  { icon: Wifi, label: "Wi-Fi", testid: "amenity-wifi" },
  { icon: AirVent, label: "Aria Condizionata", testid: "amenity-ac" },
  { icon: Heater, label: "Termosifoni", testid: "amenity-heating" },
  { icon: WashingMachine, label: "Lavatrice", testid: "amenity-washer" },
  { icon: Coffee, label: "Macchina del Caffè", testid: "amenity-coffee" },
  { icon: Microwave, label: "Microonde", testid: "amenity-microwave" },
  { icon: Wind, label: "Phon", testid: "amenity-hairdryer" },
  { icon: Layers, label: "Kit Asciugamani", testid: "amenity-towels" },
  { icon: CookingPot, label: "Padelle", testid: "amenity-pans" },
  { icon: Droplet, label: "Sale e Olio per Cucinare", testid: "amenity-salt-oil" },
];

export default function Spazi() {
  const [lightbox, setLightbox] = useState(null);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const stepLightbox = useCallback(
    (dir) => setLightbox((cur) => (cur === null ? null : (cur + dir + gallery.length) % gallery.length)),
    []
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") stepLightbox(1);
      if (e.key === "ArrowLeft") stepLightbox(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, closeLightbox, stepLightbox]);

  return (
    <section id="spazi" className="py-24 px-6 border-b border-[#2a2825] bg-[#121110]" data-testid="spazi-section">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div key={i} className="reveal-card border-t border-[#c9944a] pt-6">
              <span className="font-mono-tag text-xs text-[#c9944a]">{f.tag}</span>
              <h2 className="text-2xl font-display mt-2 mb-3">{f.title}</h2>
              <p className="text-sm text-[#a8a095] leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
          {gallery.map((p, i) => (
            <figure
              key={p.testid}
              className="relative aspect-[16/10] bg-[#1c1a18] border border-[#2a2825] overflow-hidden cursor-pointer group"
              data-testid={p.testid}
              onClick={() => setLightbox(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setLightbox(i)}
              aria-label={`Ingrandisci foto: ${p.caption}`}
            >
              <img src={p.src} alt={p.alt} loading="lazy" className={`absolute inset-0 w-full h-full object-cover ${p.position || ""} group-hover:scale-[1.03] transition-transform duration-500`} />
              <figcaption className="absolute bottom-0 left-0 right-0 p-6 pt-12 text-xs uppercase font-mono-tag tracking-wider text-[#c9944a] bg-gradient-to-t from-[#121110]/90 to-transparent pointer-events-none">
                {p.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        {lightbox !== null && (
          <div
            className="fixed inset-0 z-[100] bg-[#0c0c0b]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            data-testid="photo-lightbox"
            onClick={closeLightbox}
            role="dialog"
            aria-modal="true"
            aria-label="Galleria foto a tutto schermo"
          >
            <button
              type="button"
              data-testid="lightbox-close"
              aria-label="Chiudi galleria"
              className="absolute top-4 right-4 md:top-6 md:right-6 p-3 border border-[#3e3a35] text-[#f4efe8] hover:border-[#c9944a] hover:text-[#c9944a] transition-colors"
              onClick={closeLightbox}
            >
              <X className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="lightbox-prev"
              aria-label="Foto precedente"
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 p-3 border border-[#3e3a35] text-[#f4efe8] hover:border-[#c9944a] hover:text-[#c9944a] transition-colors"
              onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img
                src={gallery[lightbox].src}
                alt={gallery[lightbox].alt}
                className="w-full max-h-[78vh] object-contain"
                data-testid="lightbox-image"
              />
              <figcaption className="mt-4 text-center text-xs uppercase font-mono-tag tracking-widest text-[#c9944a]" data-testid="lightbox-caption">
                {gallery[lightbox].caption} · {lightbox + 1} / {gallery.length}
              </figcaption>
            </figure>
            <button
              type="button"
              data-testid="lightbox-next"
              aria-label="Foto successiva"
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 p-3 border border-[#3e3a35] text-[#f4efe8] hover:border-[#c9944a] hover:text-[#c9944a] transition-colors"
              onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-[#2a2825]" data-testid="amenities-row">
          <p className="font-mono-tag text-xs uppercase tracking-widest text-[#c9944a] mb-6">Servizi & Comfort</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {amenities.map((a) => (
              <div key={a.testid} className="flex flex-col items-center gap-3 p-5 border border-[#2a2825] bg-[#1a1816] text-center" data-testid={a.testid}>
                <a.icon className="w-6 h-6 text-[#c9944a]" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-xs font-mono-tag uppercase tracking-wider text-[#f4efe8]">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
